import React, { useState, useEffect } from 'react';
import { Calendar, Clock, User, CheckCircle, XCircle, AlertCircle, BookOpen, Edit3, Edit2, Save, X, Settings, FileText, Trash2, Video, MessageSquare, Download } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import TutorAvailabilityManager from './TutorAvailabilityManager';
import LessonResourceAssignment from './LessonResourceAssignment';
import FlexibleTimeInput from './FlexibleTimeInput';
import { formatDateTime12Hour, formatDateTimeForInput, formatDuration } from '../utils/timeFormatting';

interface Booking {
  id: string;
  startTime: string;
  endTime: string;
  type: 'LESSON' | 'CONSULTATION';
  status: 'REQUESTED' | 'CONFIRMED' | 'DENIED' | 'PENDING' | 'CANCELLED' | 'COMPLETED' | 'AMENDED';
  isOnline: boolean;
  title?: string;
  subject: {
    id: string;
    name: string;
    onlineRate: number;
    inPersonRate: number;
  };
  student: {
    id: string;
    name: string;
    email: string;
  };
  price: number;
  notes?: string;
  amendmentReason?: string;
  originalStartTime?: string;
  originalEndTime?: string;
  meetingLink?: string;
}

interface Subject {
  id: string;
  name: string;
  onlineRate: number;
  inPersonRate: number;
}

const TutorBookingManagement: React.FC = () => {
  const { user } = useAuth();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState<'requests' | 'upcoming' | 'past' | 'availability'>('requests');
  const [amendingBooking, setAmendingBooking] = useState<string | null>(null);
  const [amendmentData, setAmendmentData] = useState({
    startTime: '',
    endTime: '',
    reason: '',
    subjectId: '',
    isOnline: false
  });
  const [resourceAssignmentBooking, setResourceAssignmentBooking] = useState<string | null>(null);
  const [feedbackBooking, setFeedbackBooking] = useState<Booking | null>(null);
  const [feedbackData, setFeedbackData] = useState({
    feedback: '',
    stepsToImprove: '',
    homeworkTargets: ''
  });
  const [editingTitle, setEditingTitle] = useState<string | null>(null);
  const [titleInput, setTitleInput] = useState('');
  const [editingTeamsLink, setEditingTeamsLink] = useState<string | null>(null);
  const [teamsLinkInput, setTeamsLinkInput] = useState('');
  const [showCancelledSessions, setShowCancelledSessions] = useState(false);
  const [showDeniedSessions, setShowDeniedSessions] = useState(false);

  const isTutorSuggested = (booking: Booking) => {
    return booking.notes?.includes('[TUTOR_SUGGESTED]') || false;
  };

  const getBookingDisplayTitle = (booking: Booking) => {
    return booking.title || `${booking.subject.name} - ${booking.isOnline ? 'Online' : 'In Person'}`;
  };

  const getTutorMessage = (booking: Booking) => {
    if (!booking.notes?.includes('[TUTOR_SUGGESTED]')) return null;
    // Extract message after [TUTOR_SUGGESTED] marker
    const message = booking.notes.replace('[TUTOR_SUGGESTED]', '').trim();
    return message || 'Lesson suggested by tutor';
  };

  useEffect(() => {
    fetchBookings();
    fetchSubjects();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Remove the auto-calculation useEffect since we now use separate inputs

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const [requestsResponse, allBookingsResponse] = await Promise.all([
        api.get('/bookings/requests'),
        api.get(`/bookings/${user?.id}`)
      ]);

      // Combine and deduplicate bookings
      const requestsData = Array.isArray(requestsResponse.data) ? requestsResponse.data : [];
      const allBookingsData = Array.isArray(allBookingsResponse.data) ? allBookingsResponse.data : [];
      
      const allBookings = [
        ...requestsData,
        ...allBookingsData.filter((booking: Booking) => 
          !requestsData.some((req: Booking) => req.id === booking.id)
        )
      ];

      setBookings(allBookings);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
      setError('Failed to load bookings. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await api.get('/subjects');
      setSubjects(response.data);
    } catch (error) {
      console.error('Failed to fetch subjects:', error);
    }
  };

  const calculatePrice = (startTime: string, endTime: string, subjectId: string, isOnline: boolean, type: string = 'LESSON') => {
    if (!subjectId || !startTime || !endTime) return 0;
    
    const subject = subjects.find(s => s.id === subjectId);
    if (!subject) return 0;
    
    // Calculate duration from times
    const start = new Date(startTime);
    const end = new Date(endTime);
    const durationHours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
    
    if (durationHours <= 0) return 0;
    
    // Consultations use a fixed £5 rate on the backend regardless of duration/subject.
    if (type === 'CONSULTATION') {
      return 5;
    }

    // Regular lessons use subject-specific online/in-person rates.
    const hourlyRate = isOnline ? subject.onlineRate : subject.inPersonRate;
    return hourlyRate * durationHours;
  };

  const handleStatusUpdate = async (bookingId: string, status: 'CONFIRMED' | 'DENIED', reason?: string) => {
    try {
      await api.put(`/bookings/${bookingId}/status`, {
        status,
        amendmentReason: reason
      });

      setSuccess(`Booking ${status.toLowerCase()} successfully!`);
      
      // Force refresh of bookings to get updated state from backend
      await fetchBookings();
      
      // Hide success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to update booking status');
    }
  };

  const handleCancelBooking = async (bookingId: string) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) {
      return;
    }

    try {
      await api.put(`/bookings/${bookingId}/status`, { status: 'CANCELLED' });
      setSuccess('Booking cancelled successfully!');
      
      // Force refresh of bookings to get updated state from backend
      await fetchBookings();
      
      // Hide success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to cancel booking');
    }
  };

  const handleAmendment = async (bookingId: string) => {
    if (!amendmentData.startTime || !amendmentData.endTime || !amendmentData.subjectId) {
      setError('Please fill in all amendment fields');
      return;
    }

    try {
      const booking = filteredBookings.find(b => b.id === bookingId);
      const price = calculatePrice(amendmentData.startTime, amendmentData.endTime, amendmentData.subjectId, amendmentData.isOnline, booking?.type);
      
      // Use the tutor-amend endpoint which doesn't require ownership check
      await api.put(`/bookings/${bookingId}/tutor-amend`, {
        startTime: amendmentData.startTime,
        endTime: amendmentData.endTime,
        notes: amendmentData.reason || '',
        subjectId: amendmentData.subjectId,
        price: price
      });

      setSuccess('Booking rescheduled successfully!');
      setAmendingBooking(null);
      setAmendmentData({ startTime: '', endTime: '', reason: '', subjectId: '', isOnline: false });
      
      // Force refresh of bookings to get updated state from backend
      await fetchBookings();
      
      // Hide success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to amend booking');
    }
  };

  const startAmendment = (booking: Booking) => {
    setAmendingBooking(booking.id);
    setAmendmentData({
      startTime: formatDateTimeForInput(booking.startTime), // Format for 12-hour input
      endTime: formatDateTimeForInput(booking.endTime),
      reason: '',
      subjectId: booking.subject.id,
      isOnline: booking.isOnline
    });
  };

  const cancelAmendment = () => {
    setAmendingBooking(null);
    setAmendmentData({ startTime: '', endTime: '', reason: '', subjectId: '', isOnline: false });
  };

  const handleDeleteBooking = async (bookingId: string, studentName: string, subjectName: string) => {
    if (!window.confirm(`Are you sure you want to permanently delete the booking for ${studentName} (${subjectName})? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.delete(`/bookings/${bookingId}/permanently`);
      setSuccess('Booking permanently deleted successfully!');
      
      // Force refresh of bookings to get updated state from backend
      await fetchBookings();
      
      // Hide success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to delete booking');
    }
  };

  const handleAddFeedback = async (booking: Booking) => {
    // Check if feedback already exists
    try {
      const existingFeedback = await api.get(`/feedback/booking/${booking.id}`);
      if (existingFeedback.data) {
        // Pre-fill with existing feedback
        setFeedbackData({
          feedback: existingFeedback.data.feedback,
          stepsToImprove: existingFeedback.data.stepsToImprove,
          homeworkTargets: existingFeedback.data.homeworkTargets
        });
      } else {
        // Clear form for new feedback
        setFeedbackData({
          feedback: '',
          stepsToImprove: '',
          homeworkTargets: ''
        });
      }
    } catch (error) {
      // No existing feedback, start fresh
      setFeedbackData({
        feedback: '',
        stepsToImprove: '',
        homeworkTargets: ''
      });
    }
    
    setFeedbackBooking(booking);
  };

  const handleFeedbackSubmit = async () => {
    if (!feedbackBooking) return;

    try {
      // Check if feedback already exists
      let existingFeedback = null;
      try {
        const response = await api.get(`/feedback/booking/${feedbackBooking.id}`);
        existingFeedback = response.data;
      } catch (error) {
        // No existing feedback
      }

      if (existingFeedback) {
        // Update existing feedback
        await api.put(`/feedback/${existingFeedback.id}`, feedbackData);
        setSuccess('Feedback updated successfully!');
      } else {
        // Create new feedback
        await api.post('/feedback', {
          bookingId: feedbackBooking.id,
          ...feedbackData
        });
        setSuccess('Feedback added successfully!');
      }

      setFeedbackBooking(null);
      setFeedbackData({ feedback: '', stepsToImprove: '', homeworkTargets: '' });
      
      // Hide success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to save feedback. Please try again.');
    }
  };

  const handleCloseFeedbackModal = () => {
    setFeedbackBooking(null);
    setFeedbackData({ feedback: '', stepsToImprove: '', homeworkTargets: '' });
  };

  const startEditingTitle = (booking: Booking) => {
    setEditingTitle(booking.id);
    setTitleInput(booking.title || '');
  };

  const cancelEditingTitle = () => {
    setEditingTitle(null);
    setTitleInput('');
  };

  const saveTitle = async (bookingId: string) => {
    try {
      setError('');
      await api.put(`/bookings/${bookingId}/title`, { title: titleInput });
      
      // Refresh bookings to show updated title
      await fetchBookings();
      
      setSuccess('Lesson title updated successfully!');
      setEditingTitle(null);
      setTitleInput('');
      
      // Hide success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to update title. Please try again.');
    }
  };

  const startEditingTeamsLink = (booking: Booking) => {
    setEditingTeamsLink(booking.id);
    setTeamsLinkInput(booking.meetingLink || '');
  };

  const cancelEditingTeamsLink = () => {
    setEditingTeamsLink(null);
    setTeamsLinkInput('');
  };

  const saveTeamsLink = async (bookingId: string) => {
    try {
      setError('');
      await api.put(`/bookings/${bookingId}/teams-link`, { meetingLink: teamsLinkInput });
      
      // Refresh bookings to show updated link
      await fetchBookings();
      
      setSuccess('Teams link updated successfully!');
      setEditingTeamsLink(null);
      setTeamsLinkInput('');
      
      // Hide success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to update Teams link. Please try again.');
    }
  };

  const formatDateTime = (dateString: string) => {
    return formatDateTime12Hour(dateString);
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-GB', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Europe/London'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'REQUESTED': return 'bg-ochre/15 text-ochre';
      case 'CONFIRMED': return 'bg-emerald-100 text-emerald-800';
      case 'DENIED': return 'bg-red-100 text-red-800';
      case 'AMENDED': return 'bg-navy/15 text-navy';
      case 'CANCELLED': return 'bg-cream text-muted';
      case 'COMPLETED': return 'bg-navy/10 text-navy';
      default: return 'bg-cream text-muted';
    }
  };

  const getStatusDisplayText = (status: string, hasAmendmentReason: boolean) => {
    if (status === 'AMENDED' && hasAmendmentReason) {
      return 'AMENDED (CONFIRMED → REVIEW)';
    }
    return status;
  };

  const handleDownloadICS = async (bookingId: string) => {
    try {
      const response = await api.get(`/bookings/${bookingId}/ics`, {
        responseType: 'blob',
      });

      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'text/calendar' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Get filename from response headers or create default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'lesson.ics';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Failed to download ICS file:', error);
      setError('Failed to download calendar file. Please try again.');
    }
  };

  // Filter bookings based on active tab
  const filteredBookings = Array.isArray(bookings) ? bookings.filter(booking => {
    const now = new Date();
    const bookingStart = new Date(booking.startTime);
    
    switch (activeTab) {
      case 'requests':
        return ['REQUESTED', 'AMENDED'].includes(booking.status);
      case 'upcoming':
        return ['CONFIRMED'].includes(booking.status) && bookingStart > now;
      case 'past':
        const isPastBooking = ['COMPLETED', 'CANCELLED', 'DENIED'].includes(booking.status) || 
                             (['CONFIRMED'].includes(booking.status) && bookingStart < now);
        
        if (!isPastBooking) return false;
        
        // Apply filters for past sessions
        if (booking.status === 'CANCELLED' && !showCancelledSessions) return false;
        if (booking.status === 'DENIED' && !showDeniedSessions) return false;
        
        return true;
      case 'availability':
        return false; // Not used for availability tab
      default:
        return true;
    }
  }).sort((a, b) => {
    // Sort past sessions by most recent first
    if (activeTab === 'past') {
      return new Date(b.startTime).getTime() - new Date(a.startTime).getTime();
    }
    // Keep original order for other tabs
    return 0;
  }) : [];

  if (!user || user.role !== 'TUTOR') {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-cream border border-rule rounded-card p-4 flex items-center">
          <AlertCircle className="w-5 h-5 text-ochre mr-3" />
          <p className="text-ink">Access denied. Tutor role required.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-ink mb-4">Booking Management</h1>
        <p className="text-muted">
          Manage student booking requests and view your schedule.
        </p>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-card p-4 flex items-center">
          <CheckCircle className="w-5 h-5 text-emerald-600 mr-3 flex-shrink-0" />
          <p className="text-emerald-800">{success}</p>
        </div>
      )}

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-card p-4 flex items-center">
          <AlertCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0" />
          <p className="text-red-800">{error}</p>
          <button
            onClick={() => setError('')}
            className="ml-auto text-red-600 hover:text-red-800"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-rule mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('requests')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'requests'
                ? 'border-ochre text-ochre'
                : 'border-transparent text-muted hover:text-ink hover:border-rule'
            }`}
          >
            <AlertCircle className="w-4 h-4 inline mr-2" />
            Pending Requests
            {filteredBookings.length > 0 && activeTab !== 'requests' && (
              <span className="ml-1 bg-ochre/15 text-ochre text-xs px-2 py-1 rounded-full">
                {Array.isArray(bookings) ? bookings.filter(b => ['REQUESTED', 'AMENDED'].includes(b.status)).length : 0}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'upcoming'
                ? 'border-ochre text-ochre'
                : 'border-transparent text-muted hover:text-ink hover:border-rule'
            }`}
          >
            <Calendar className="w-4 h-4 inline mr-2" />
            Upcoming Sessions
          </button>
          <button
            onClick={() => setActiveTab('past')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'past'
                ? 'border-ochre text-ochre'
                : 'border-transparent text-muted hover:text-ink hover:border-rule'
            }`}
          >
            <Clock className="w-4 h-4 inline mr-2" />
            Past Sessions
          </button>
          <button
            onClick={() => setActiveTab('availability')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'availability'
                ? 'border-ochre text-ochre'
                : 'border-transparent text-muted hover:text-ink hover:border-rule'
            }`}
          >
            <Settings className="w-4 h-4 inline mr-2" />
            Availability Settings
          </button>
        </nav>
      </div>

      {/* Content */}
      {activeTab === 'availability' ? (
        <TutorAvailabilityManager />
      ) : loading ? (
        <div className="bg-paper border border-rule rounded-card p-8">
          <div className="animate-pulse space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="border border-rule rounded-card p-6">
                <div className="h-4 bg-cream rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-cream rounded w-1/2 mb-2"></div>
                <div className="h-3 bg-cream rounded w-2/3"></div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <>
          {/* Past Sessions Filter Controls */}
          {activeTab === 'past' && (
            <div className="bg-paper border border-rule rounded-card p-4 mb-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-ink">Session Filters</h3>
                <button
                  onClick={() => {
                    setShowCancelledSessions(false);
                    setShowDeniedSessions(false);
                  }}
                  className="text-sm text-muted hover:text-ink"
                >
                  Clear Filters
                </button>
              </div>
              <div className="mt-3 flex flex-wrap gap-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={showCancelledSessions}
                    onChange={(e) => setShowCancelledSessions(e.target.checked)}
                    className="h-4 w-4 text-ochre focus:ring-ochre border-rule rounded"
                  />
                  <span className="ml-2 text-sm text-ink">Show cancelled sessions</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={showDeniedSessions}
                    onChange={(e) => setShowDeniedSessions(e.target.checked)}
                    className="h-4 w-4 text-ochre focus:ring-ochre border-rule rounded"
                  />
                  <span className="ml-2 text-sm text-ink">Show denied sessions</span>
                </label>
              </div>
            </div>
          )}

          {/* Bookings List */}
          <div className="bg-paper border border-rule rounded-card">
            {!Array.isArray(filteredBookings) || filteredBookings.length === 0 ? (
            <div className="p-8 text-center">
              <div className="mx-auto w-12 h-12 bg-cream rounded-full flex items-center justify-center mb-4">
                <Calendar className="w-6 h-6 text-muted" />
              </div>
              <h3 className="text-lg font-medium text-ink mb-2">No bookings found</h3>
              <p className="text-muted">
                {activeTab === 'requests' && 'No pending booking requests at the moment.'}
                {activeTab === 'upcoming' && 'No upcoming sessions scheduled.'}
                {activeTab === 'past' && 'No past sessions to display.'}
              </p>
            </div>
          ) : (
            <div className={activeTab === 'upcoming' ? 'p-6 space-y-4' : 'divide-y divide-rule'}>
              {Array.isArray(filteredBookings) ? filteredBookings.map((booking) => (
                <div key={booking.id} className={`${activeTab === 'upcoming' ? '' : 'p-6'}`}>
                  {activeTab === 'upcoming' ? (
                    // Exact 1:1 copy of dashboard upcoming sessions UI
                    <div className={`border rounded-card p-4 ${
                      isTutorSuggested(booking) && booking.status === 'REQUESTED'
                        ? 'border-emerald-300 bg-emerald-50'
                        : 'border-rule'
                    }`}>
                      {isTutorSuggested(booking) && booking.status === 'REQUESTED' && (
                        <div className="mb-3 p-2 bg-emerald-100 border border-emerald-200 rounded-md">
                          <p className="text-sm font-medium text-emerald-900 mb-1">
                            📤 Your Lesson Suggestion
                          </p>
                          <p className="text-xs text-emerald-700 mb-2">
                            You suggested this lesson time to {booking.student.name}. Waiting for their response.
                          </p>
                          {getTutorMessage(booking) && getTutorMessage(booking) !== 'Lesson suggested by tutor' && (
                            <div className="bg-paper border border-emerald-200 rounded-md p-2">
                              <p className="text-xs font-medium text-emerald-800 mb-1">Your message:</p>
                              <p className="text-sm text-emerald-900 italic">"{getTutorMessage(booking)}"</p>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          {editingTitle === booking.id ? (
                            <div className="flex items-center gap-2 mb-2">
                              <input
                                type="text"
                                value={titleInput}
                                onChange={(e) => setTitleInput(e.target.value)}
                                placeholder={`${booking.subject.name} - ${booking.isOnline ? 'Online' : 'In Person'}`}
                                className="input flex-1 font-semibold"
                              />
                              <button
                                onClick={() => saveTitle(booking.id)}
                                className="btn-primary px-3 py-1 text-sm"
                              >
                                Save
                              </button>
                              <button
                                onClick={cancelEditingTitle}
                                className="btn-secondary px-3 py-1 text-sm"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-ink">
                                {getBookingDisplayTitle(booking)}
                              </h3>
                              <button
                                onClick={() => startEditingTitle(booking)}
                                className="text-muted hover:text-ink p-1"
                                title="Edit lesson title"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                          <p className="text-sm text-muted">
                            with {booking.student.name}
                          </p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
                          {isTutorSuggested(booking) && booking.status === 'REQUESTED' ? 'SUGGESTED' : getStatusDisplayText(booking.status, !!booking.amendmentReason)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <p className="text-sm text-muted">
                          {formatDateTime(booking.startTime)}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-3">
                        {editingTeamsLink === booking.id ? (
                          <div className="flex items-center gap-2 w-full mb-2">
                            <input
                              type="url"
                              value={teamsLinkInput}
                              onChange={(e) => setTeamsLinkInput(e.target.value)}
                              placeholder="Enter Teams meeting link"
                              className="input flex-1 text-sm"
                            />
                            <button
                              onClick={() => saveTeamsLink(booking.id)}
                              className="btn-primary px-3 py-1 text-sm"
                            >
                              Save
                            </button>
                            <button
                              onClick={cancelEditingTeamsLink}
                              className="btn-secondary px-3 py-1 text-sm"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <>
                            {booking.meetingLink && (
                              <a
                                href={booking.meetingLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center text-sm text-ochre hover:text-ochre/80 bg-ochre/10 px-3 py-1 rounded-md"
                              >
                                <Video className="w-4 h-4 mr-1" />
                                Teams Link
                              </a>
                            )}
                            <button
                              onClick={() => startEditingTeamsLink(booking)}
                              className="flex items-center text-sm text-ochre hover:text-ochre/80 bg-ochre/10 px-3 py-1 rounded-md"
                              title={booking.meetingLink ? "Edit Teams link" : "Add Teams link"}
                            >
                              <Video className="w-4 h-4 mr-1" />
                              {booking.meetingLink ? 'Edit Link' : 'Add Link'}
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => startAmendment(booking)}
                          className="flex items-center text-sm text-ink hover:text-ochre bg-cream px-3 py-1 rounded-md border border-rule"
                        >
                          <Edit3 className="w-4 h-4 mr-1" />
                          Amend
                        </button>
                        <button
                          onClick={() => handleCancelBooking(booking.id)}
                          className="flex items-center text-sm text-red-600 hover:text-red-700 bg-red-50 px-3 py-1 rounded-md"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Cancel
                        </button>
                        {booking.status === 'CONFIRMED' && (
                          <>
                            <button
                              onClick={() => setResourceAssignmentBooking(booking.id)}
                              className="flex items-center text-sm text-ochre hover:text-ochre/80 bg-ochre/10 px-3 py-1 rounded-md"
                            >
                              <FileText className="w-4 h-4 mr-1" />
                              Assign Resources
                            </button>
                            <button
                              className="flex items-center text-sm text-navy hover:text-navy/80 bg-navy/10 px-3 py-1 rounded-md"
                            >
                              <MessageSquare className="w-4 h-4 mr-1" />
                              Add Feedback
                            </button>
                            <button
                              onClick={() => handleDownloadICS(booking.id)}
                              className="flex items-center text-sm text-emerald-700 hover:text-emerald-800 bg-emerald-50 px-3 py-1 rounded-md"
                              title="Download calendar file for Apple Calendar"
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Add to Calendar
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ) : (
                    // Original detailed layout for other tabs
                    <div>
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            {editingTitle === booking.id ? (
                              <div className="flex items-center gap-2 flex-1">
                                <input
                                  type="text"
                                  value={titleInput}
                                  onChange={(e) => setTitleInput(e.target.value)}
                                  placeholder={`${booking.subject.name} - ${booking.isOnline ? 'Online' : 'In Person'}`}
                                  className="input flex-1 text-lg font-semibold"
                                />
                                <button
                                  onClick={() => saveTitle(booking.id)}
                                  className="btn-primary px-3 py-1 text-sm"
                                >
                                  Save
                                </button>
                                <button
                                  onClick={cancelEditingTitle}
                                  className="btn-secondary px-3 py-1 text-sm"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 flex-1">
                                <h3 className="text-lg font-semibold text-ink">
                                  {getBookingDisplayTitle(booking)}
                                </h3>
                                <button
                                  onClick={() => startEditingTitle(booking)}
                                  className="text-muted hover:text-ink p-1"
                                  title="Edit lesson title"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
                              {getStatusDisplayText(booking.status, !!booking.amendmentReason)}
                            </span>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-muted">
                            <div className="flex items-center">
                              <User className="w-4 h-4 mr-2" />
                              <span>{booking.student.name} ({booking.student.email})</span>
                            </div>

                            <div className="flex items-center">
                              <Clock className="w-4 h-4 mr-2" />
                              <span>
                                {formatDateTime(booking.startTime)} - {formatTime(booking.endTime)}
                              </span>
                            </div>

                            <div className="flex items-center">
                              <BookOpen className="w-4 h-4 mr-2" />
                              <span>£{booking.price} ({booking.isOnline ? 'online' : 'in-person'})</span>
                            </div>

                            {booking.meetingLink && (
                              <div className="flex items-center">
                                <span className="text-emerald-700">Online Session</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Notes and Amendment Info for non-upcoming tabs */}
                  {activeTab !== 'upcoming' && (
                    <>
                      {booking.notes && (
                        <div className="mt-3 p-3 bg-cream rounded-card border border-rule">
                          <p className="text-sm text-ink">
                            <span className="font-medium">Student Notes:</span> {booking.notes}
                          </p>
                        </div>
                      )}

                      {booking.amendmentReason && (
                        <div className="mt-3 p-3 bg-navy/10 rounded-card border border-rule">
                          <p className="text-sm text-navy">
                            <span className="font-medium">Amendment Reason:</span> {booking.amendmentReason}
                          </p>
                          {booking.originalStartTime && (
                            <p className="text-sm text-navy/80 mt-1">
                              <span className="font-medium">Original Time:</span> {formatDateTime(booking.originalStartTime)} - {formatTime(booking.originalEndTime!)}
                            </p>
                          )}
                          {booking.status === 'AMENDED' && (
                            <p className="text-sm text-navy/80 mt-1 font-medium">
                              ⚠️ This lesson was previously confirmed and has been amended - please review and accept the changes.
                            </p>
                          )}
                        </div>
                      )}

                      {/* Teams Link for Past Sessions */}
                      {activeTab === 'past' && booking.meetingLink && (
                        <div className="mt-3">
                          <a
                            href={booking.meetingLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-sm text-ochre hover:text-ochre/80 bg-ochre/10 px-3 py-1 rounded-md"
                          >
                            <Video className="w-4 h-4 mr-1" />
                            Teams Link
                          </a>
                        </div>
                      )}
                    </>
                  )}

                  {/* Amendment Form */}
                  {amendingBooking === booking.id && (
                    <div className="mt-4 p-4 bg-cream border border-rule rounded-card">
                      <h4 className="font-medium text-ink mb-3">Amend Booking</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <FlexibleTimeInput
                          label="New Start Time"
                          value={amendmentData.startTime}
                          onChange={(value) => setAmendmentData(prev => ({ ...prev, startTime: value }))}
                        />

                        <FlexibleTimeInput
                          label="New End Time"
                          value={amendmentData.endTime}
                          onChange={(value) => setAmendmentData(prev => ({ ...prev, endTime: value }))}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-ink mb-1">
                            Subject
                          </label>
                          <select
                            value={amendmentData.subjectId}
                            onChange={(e) => setAmendmentData(prev => ({ ...prev, subjectId: e.target.value }))}
                            className="input w-full"
                          >
                            <option value="">Select a subject</option>
                            {subjects.map(subject => (
                              <option key={subject.id} value={subject.id}>
                                {subject.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-ink mb-1">
                            Lesson Type
                          </label>
                          <select
                            value={amendmentData.isOnline ? 'online' : 'inperson'}
                            onChange={(e) => setAmendmentData(prev => ({ ...prev, isOnline: e.target.value === 'online' }))}
                            className="input w-full"
                          >
                            <option value="online">Online</option>
                            <option value="inperson">In-Person</option>
                          </select>
                        </div>
                      </div>

                      {/* Price Display */}
                      {amendmentData.subjectId && amendmentData.startTime && amendmentData.endTime && (() => {
                        const startTime = new Date(amendmentData.startTime);
                        const endTime = new Date(amendmentData.endTime);
                        const durationHours = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60);
                        const durationMinutes = Math.round(durationHours * 60);
                        const newPrice = calculatePrice(amendmentData.startTime, amendmentData.endTime, amendmentData.subjectId, amendmentData.isOnline, booking.type);

                        return (
                          <div className="mb-4 p-3 bg-paper border border-rule rounded-card">
                            <p className="text-sm font-medium text-ink">
                              New Price: £{newPrice.toFixed(2)}
                            </p>
                            <p className="text-xs text-muted mt-1">
                              Duration: {formatDuration(durationHours)} • Original Price: £{booking.price.toFixed(2)}
                            </p>
                            <p className="text-xs text-muted mt-1">
                              {startTime.toLocaleString('en-GB', {
                                weekday: 'short', month: 'short', day: 'numeric',
                                hour: 'numeric', minute: '2-digit', hour12: true
                              })} - {endTime.toLocaleTimeString('en-GB', {
                                hour: 'numeric', minute: '2-digit', hour12: true
                              })}
                            </p>
                          </div>
                        );
                      })()}
                      <div className="flex space-x-3">
                        <button
                          onClick={() => handleAmendment(booking.id)}
                          className="btn btn-primary flex items-center"
                        >
                          <Save className="w-4 h-4 mr-2" />
                          Save Amendment
                        </button>
                        <button
                          onClick={cancelAmendment}
                          className="btn btn-secondary flex items-center"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons - Only show for non-upcoming tabs since upcoming has inline buttons */}
                  {activeTab !== 'upcoming' && (
                    <>
                      {/* Action Buttons for Pending Bookings */}
                      {['REQUESTED', 'AMENDED'].includes(booking.status) && (
                        <div className="mt-4 flex flex-wrap gap-3">
                          <button
                            onClick={() => handleStatusUpdate(booking.id, 'CONFIRMED')}
                            className="btn bg-emerald-600 text-white hover:bg-emerald-700 flex items-center"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Accept
                          </button>

                          <button
                            onClick={() => handleStatusUpdate(booking.id, 'DENIED')}
                            className="btn bg-red-600 text-white hover:bg-red-700 flex items-center"
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Deny
                          </button>

                          {amendingBooking !== booking.id && (
                            <button
                              onClick={() => startAmendment(booking)}
                              className="btn btn-outline flex items-center"
                            >
                              <Edit3 className="w-4 h-4 mr-2" />
                              Amend Time
                            </button>
                          )}
                        </div>
                      )}

                      {/* Action Buttons for Confirmed Lessons */}
                      {booking.status === 'CONFIRMED' && (
                        <div className="mt-4 flex flex-wrap gap-3">
                          <button
                            onClick={() => setResourceAssignmentBooking(booking.id)}
                            className="btn btn-primary flex items-center"
                          >
                            <FileText className="w-4 h-4 mr-2" />
                            Assign Resources
                          </button>

                          {amendingBooking !== booking.id && (
                            <button
                              onClick={() => startAmendment(booking)}
                              className="btn btn-outline flex items-center"
                            >
                              <Edit3 className="w-4 h-4 mr-2" />
                              Amend Time
                            </button>
                          )}

                          <button
                            onClick={() => handleDownloadICS(booking.id)}
                            className="btn btn-outline flex items-center text-emerald-700 border-emerald-600 hover:bg-emerald-50"
                            title="Download calendar file for Apple Calendar"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Add to Calendar
                          </button>
                        </div>
                      )}

                      {/* Action Buttons for Past Sessions (Feedback & Delete) */}
                      {activeTab === 'past' && (
                        <div className="mt-4 flex flex-wrap gap-3">
                          <button
                            onClick={() => handleAddFeedback(booking)}
                            className="btn bg-ochre text-white hover:bg-ochre/90 flex items-center"
                          >
                            <MessageSquare className="w-4 h-4 mr-2" />
                            Add Feedback
                          </button>
                          <button
                            onClick={() => handleDeleteBooking(booking.id, booking.student.name, booking.subject.name)}
                            className="btn bg-red-600 text-white hover:bg-red-700 flex items-center"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete Permanently
                          </button>
                        </div>
                      )}
                    </>
                  )}

                </div>
              )) : null}
            </div>
          )}
        </div>
        </>
      )}

      {/* Resource Assignment Modal */}
      <LessonResourceAssignment
        bookingId={resourceAssignmentBooking || ''}
        isOpen={!!resourceAssignmentBooking}
        onClose={() => setResourceAssignmentBooking(null)}
        onUpdate={fetchBookings}
      />

      {/* Feedback Modal */}
      {feedbackBooking && (
        <div className="fixed inset-0 bg-ink/40 flex items-center justify-center z-50 p-4">
          <div className="bg-paper border border-rule rounded-card max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-rule">
              <h2 className="text-xl font-semibold text-ink">
                Add Feedback for {feedbackBooking.student.name}
              </h2>
              <button
                onClick={handleCloseFeedbackModal}
                className="text-muted hover:text-ink transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Lesson Info */}
              <div className="bg-cream border border-rule rounded-card p-4">
                <h3 className="font-medium text-ink mb-2">Lesson Details</h3>
                <div className="text-sm text-muted space-y-1">
                  <p><strong>Subject:</strong> {feedbackBooking.subject.name}</p>
                  <p><strong>Date:</strong> {formatDateTime(feedbackBooking.startTime)}</p>
                  <p><strong>Type:</strong> {feedbackBooking.type}</p>
                </div>
              </div>

              {/* Feedback Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-ink mb-2">
                    General Feedback
                  </label>
                  <textarea
                    value={feedbackData.feedback}
                    onChange={(e) => setFeedbackData(prev => ({ ...prev, feedback: e.target.value }))}
                    rows={4}
                    className="input w-full"
                    placeholder="How did the lesson go? What did the student do well?"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-ink mb-2">
                    Steps to Improve
                  </label>
                  <textarea
                    value={feedbackData.stepsToImprove}
                    onChange={(e) => setFeedbackData(prev => ({ ...prev, stepsToImprove: e.target.value }))}
                    rows={3}
                    className="input w-full"
                    placeholder="What specific areas should the student work on?"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-ink mb-2">
                    Homework Targets
                  </label>
                  <textarea
                    value={feedbackData.homeworkTargets}
                    onChange={(e) => setFeedbackData(prev => ({ ...prev, homeworkTargets: e.target.value }))}
                    rows={3}
                    className="input w-full"
                    placeholder="What should the student focus on for homework?"
                  />
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={handleCloseFeedbackModal}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleFeedbackSubmit}
                  className="btn btn-primary"
                >
                  Save Feedback
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TutorBookingManagement;